// src/lib/rbac/permissions.ts

import { type RoleType } from './roles';

// ─── SOURCE OF TRUTH ──────────────────────────────────────────────────
// The ROLE_PERMISSIONS block below is GENERATED from the BE file:
//   elixaa-core/elixaa-api/src/configs/permissionsConfig.ts
//
// Run `pnpm permissions:sync` to regenerate after BE changes.
// CI runs `pnpm permissions:check` to detect drift; build fails if FE
// is out of date.
//
// Permission format:
//   module:action           → full access within BA scope
//   module:action_<scope>   → restricted; service layer enforces scope
//   module:*                → all actions on that module within BA
//   *                       → god mode (platform super_admin only)
//
// Known scope suffixes: _own, _assigned, _basic, _profile
// Known action qualifiers: _phi
// ─────────────────────────────────────────────────────────────────────

// ─── FE-SPECIFIC CONTEXT (above the synced matrix) ──────────────────
//
// chief_psychologist:
//   No longer BA-wide — scoped via PractitionerAssignment per a690359.
//
// psychologist:
//   case_report:review is required for psychologists with isLead=true
//   on a project row. Service layer enforces
//   report.reviewerId === requestUser.id, so a non-lead psy holding this
//   perm cannot approve arbitrary reports. See a690359 §6.2.
//
// org_user:
//   BE explicitly denies at resource-scope middleware until a scope
//   table lands (a690359 §6.3, §11). Matrix mirrors BE truth so the FE
//   permission gate matches the route gate, but downstream calls
//   currently 403 — this is intentional pending the scope table follow-up.
//
// ─────────────────────────────────────────────────────────────────────

// === ROLE_PERMISSIONS: SYNCED FROM BE — DO NOT EDIT MANUALLY ===
// === Run `pnpm permissions:sync` to update from elixaa-core ===
export const ROLE_PERMISSIONS = {
  // Platform-level: Elixaa team - full system access
  super_admin: ['*'],

  // Business Associate super admin: full control within their Business Associate
  ba_super_admin: [
    'admin_list:read',
    'ba:read',
    // Ozonetel telephony pool (spec docs/superpowers/specs/2026-09-06-ozonetel-telephony-design.md).
    'telephony:read',
    'telephony:dismiss',
    'telephony:reassign',
    'did_mapping:read',
    'did_mapping:manage',
    'ba:update',
    'org:*',
    'location:*',
    'user:*',
    'practitioner:*',
    'client:*',
    'session:*',
    'session_note:create',
    'session_note:update',
    'session_note:read',
    'case_report:create',
    'case_report:read',
    'case_report:update',
    'assessment:*',
    'engagement:*',
    'event:*',
    'report:*',
    'red_flag:read',
    'red_flag:create',
    'feedback:*',
    'dashboard:*',
    'stats:*',
    'compliance:*',
    'audit:read',
  ],

  // Business Associate admin: all features except destructive ops and audit
  ba_admin: [
    'admin_list:read',
    'ba:read',
    // Ozonetel telephony pool (spec docs/superpowers/specs/2026-09-06-ozonetel-telephony-design.md).
    'telephony:read',
    'telephony:dismiss',
    'telephony:reassign',
    'did_mapping:read',
    'ba:update',
    'org:read',
    'org:create',
    'org:update',
    'location:read',
    'location:create',
    'location:update',
    'user:read',
    'user:create',
    'user:update',
    'user:invite',
    'practitioner:read',
    'practitioner:update',
    'practitioner:assign',
    'client:read',
    'client:create',
    'client:update',
    'session:read',
    'session:create',
    'session:update',
    'session_note:read',
    'case_report:read',
    'assessment:*',
    'engagement:*',
    'event:*',
    'report:read',
    'red_flag:read',
    'feedback:read',
    'dashboard:read',
    'stats:read',
    'compliance:read',
  ],

  // Chief psychologist: oversight scoped to (location, project) via PractitionerAssignment.
  // Reads/approves reports for psychologists at the same location+project they're assigned to.
  chief_psychologist: [
    'ba:read',
    // Ozonetel telephony pool (spec docs/superpowers/specs/2026-09-06-ozonetel-telephony-design.md).
    'telephony:read',
    'telephony:claim',
    'telephony:dial',
    'telephony:dismiss',
    'telephony:reassign',
    'org:read_assigned',
    'location:read_assigned',
    'practitioner:read_assigned',
    'practitioner:update_own',
    'client:read_assigned',
    'client:read_phi_assigned',
    // Dormant (SHADOW) client quick-create from the helpline/backfill flow.
    // Scoped variant: the service layer enforces the PractitionerAssignment
    // location gate, which is what _assigned means here.
    // Route: POST /clients/shadow. Service: clientService.createShadowClient.
    'client:create_assigned',
    // Lets the chief reach PATCH /clients/:id for risk updates from the
    // session screen and the client detail page. Field-level scope (risk
    // only, no PHI gating change) is enforced in clientService.updateClient.
    'client:update_assigned',
    'session:read_assigned',
    'session:create',
    'session:update_own',
    'session_note:read_assigned',
    'session_note:create_own',
    'session_note:update_own',
    'case_report:read_assigned',
    'case_report:create_own',
    'case_report:update_own',
    'case_report:review',
    'assessment:read_assigned',
    'assessment:assign_own',
    'assessment:fill',
    'engagement:read_assigned',
    'engagement:assign_own',
    'engagement:review_assigned',
    // Chat inbox: respond to student chats at assigned locations.
    'chat:respond',
    'red_flag:read',
    'red_flag:create',
    'red_flag:close',
    'feedback:read_assigned',
    'dashboard:read_own',
    'report:approve',
    'report:reject',
    'profile:read_own',
    'profile:update_own',
  ],

  // Psychologist: own clients only. Lead status (PractitionerAssignmentProject.isLead,
  // per project) grants the same perms; the lead distinction only affects case-report
  // approval routing.
  // Note: `session:read_*` permissions are broadened at the query layer in
  // sessionService.listSessions for psychologists with active
  // PractitionerAssignmentProject.isLead = true rows. The widening is
  // data-driven (per-project lead flag) rather than role-static, so it is
  // enforced in the service WHERE clause rather than as a separate
  // permission string here. See spec 2026-05-20 §Backend §1.
  psychologist: [
    'ba:read',
    // Ozonetel telephony pool (spec docs/superpowers/specs/2026-09-06-ozonetel-telephony-design.md).
    'telephony:read',
    'telephony:claim',
    'telephony:dial',
    'telephony:dismiss',
    'org:read_own',
    'location:read_own',
    'practitioner:read_own',
    'practitioner:update_own',
    'client:read_own',
    'client:read_phi_own',
    // Dormant (SHADOW) client quick-create from the helpline/backfill flow.
    // Scoped variant: the service layer enforces the PractitionerAssignment
    // location gate.
    // Route: POST /clients/shadow. Service: clientService.createShadowClient.
    'client:create_own',
    // Lets the psychologist reach PATCH /clients/:id for risk updates from the
    // session screen and the client detail page. Field-level scope (risk
    // only, no PHI gating change) is enforced in clientService.updateClient.
    'client:update_own',
    'session:read_own',
    'session:create_own',
    'session:update_own',
    'session_note:read_own',
    'session_note:create_own',
    'session_note:update_own',
    'case_report:read_own',
    'case_report:create_own',
    'case_report:update_own',
    // Required for lead psychologists (isLead=true on a project row) to act on
    // case reports routed to them. The route gate fires before service-layer
    // routing logic, so without this perm a lead cannot approve/reject. The
    // service layer still enforces report.reviewerId === requestUser.id, so a
    // non-lead psychologist holding this perm cannot approve arbitrary reports.
    'case_report:review',
    'assessment:read_own',
    'assessment:assign_own',
    'assessment:fill',
    'engagement:read_own',
    'engagement:assign_own',
    'engagement:review_own',
    // Chat inbox: respond to student chats at assigned locations.
    'chat:respond',
    'red_flag:read',
    'red_flag:create',
    'feedback:read_own',
    'dashboard:read_own',
    'profile:read_own',
    'profile:update_own',
  ],

  // Volunteer: campus-support, online-only practitioner. Locked to a single
  // location at create time (immutable; one row in PractitionerAssignment, ever).
  // Same clinical-write surface as psychologist: own clients, own sessions, own
  // notes, own case reports (auto-published on submit, no review). Raises red
  // flags but cannot close them (chief-only). Cannot use the admin assignment
  // endpoints or the existing /practitioners/availability/search.
  // Wider case-report read access is handled in caseReportService.listReports:
  // any practitioner at the volunteer's location can read volunteer-authored
  // reports without needing a separate permission token.
  volunteer: [
    'ba:read',
    'org:read_own',
    'location:read_own',
    'practitioner:read_own',
    'practitioner:update_own',
    'client:read_own',
    'client:read_phi_own',
    'session:read_own',
    'session:create_own',
    'session:update_own',
    'session_note:read_own',
    'session_note:create_own',
    'session_note:update_own',
    'case_report:read_own',
    'case_report:create_own',
    'case_report:update_own',
    'assessment:read_own',
    'assessment:assign_own',
    'assessment:fill',
    'red_flag:read',
    'red_flag:create',
    'feedback:read_own',
    'dashboard:read_own',
    'profile:read_own',
    'profile:update_own',
  ],

  // Customer support: booking + client profile only, NO clinical data
  customer_support: [
    'ba:read',
    'org:read_basic',
    'location:read_basic',
    'client:read_basic',
    'client:create',
    'client:update_profile',
    'session:create',
    'session:read_basic',
    'dashboard:read_basic',
    'profile:read_own',
    'profile:update_own',
  ],

  // Org user: org-side stakeholder (employer/sponsor rep). Read-only analytics
  // for the location(s) they are attached to within a single organization. NO PHI.
  org_user: [
    'ba:read',
    'org:read_assigned',
    'location:read_assigned',
    'dashboard:read_assigned',
    'stats:read_assigned',
    'compliance:read_assigned',
    'engagement:read_assigned',
    'feedback:read_assigned',
    'profile:read_own',
    'profile:update_own',
  ],

  // Client: own data, bookings, assessments
  client: [
    'ba:read',
    'org:read_own',
    'location:read_own',
    'client:read_own',
    'client:read_phi_own',
    'client:update_own',
    'session:create_own',
    'session:read_own',
    'session:update_own',
    'assessment:fill_own',
    'assessment:read_own',
    'engagement:view',
    'engagement:submit_own',
    'mood:own',
    // Student chat: create / read / message / end own chat sessions.
    'chat:own',
    'feedback:create_own',
    'feedback:read_own',
    'profile:read_own',
    'profile:update_own',
  ],
} as const satisfies Record<RoleType, readonly string[]>;
// === END SYNCED ===
//
// NOTE: see FE-SPECIFIC CONTEXT block above for operational caveats on
// `org_user` (403 pending scope table) and `psychologist` (lead-only
// case_report:review enforcement).

/**
 * Permissions defined on the backend (and accepted by `requirePermission`)
 * that aren't listed verbatim in any role grant — they're conferred via
 * `module:*` wildcards. We list them here so call sites can pass the
 * specific literal (e.g. `practitioner:delete`) without TS rejecting it.
 *
 * Runtime check still uses `hasPermission`, which expands wildcards
 * correctly. This list is purely for type-system completeness.
 */
const _WILDCARD_GRANTED_PERMISSIONS = [
  'practitioner:create',
  'practitioner:delete',
  'client:delete',
  'org:delete',
  'location:delete',
  'user:delete',
  'session:delete',
  'assessment:read',
  'assessment:assign',
  'assessment:create',
  'assessment:update',
  'assessment:delete',
  'engagement:read',
  'engagement:assign',
  'engagement:review',
  'engagement:create',
  'engagement:update',
  'engagement:delete',
] as const;

/**
 * Union of every permission string a typed call site may pass.
 * Includes both explicit grants (from the matrix) and wildcard-only grants.
 */
export type Permission =
  | (typeof ROLE_PERMISSIONS)[RoleType][number]
  | (typeof _WILDCARD_GRANTED_PERMISSIONS)[number];
